import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const StyledPageNotFoundIllustration = styled.svg`
  --color-gray-50: ${({ theme }) => theme.color.gray[50].value};
  --color-gray-100: ${({ theme }) => theme.color.gray[100].value};
  --color-gray-200: ${({ theme }) => theme.color.gray[200].value};
  --color-gray-300: ${({ theme }) => theme.color.gray[300].value};
  --color-gray-400: ${({ theme }) => theme.color.gray[400].value};
  --color-gray-500: ${({ theme }) => theme.color.gray[500].value};
  --color-gray-600: ${({ theme }) => theme.color.gray[600].value};
  --color-gray-700: ${({ theme }) => theme.color.gray[700].value};
  --color-gray-800: ${({ theme }) => theme.color.gray[800].value};
  --color-gray-900: ${({ theme }) => theme.color.gray[900].value};

  .sun,
  .windows {
    fill: var(--color-gray-50);
  }

  .bird,
  .bird-2,
  .cloud,
  .grass-1,
  .grass-2,
  .tree {
    fill: var(--color-gray-100);
  }

  .rectangle-49,
  .rock,
  .rock-2 {
    fill: var(--color-gray-200);
  }

  .rectangle-33,
  .rectangle-53,
  .rectangle-56,
  .rectangle-57,
  .top {
    fill: var(--color-gray-300);
  }
`;

const PageNotFoundIllustration = ({
  className = undefined,
  dataTestId = undefined,
  size = 400,
  svgTitle = 'Page Not Found',
  ...other
}) => {
  return (
    <StyledPageNotFoundIllustration
      className={className}
      data-testid={dataTestId ? `${dataTestId}-page-not-found` : 'page-not-found-svg'}
      fill="none"
      height={size}
      viewBox="0 0 400 400"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...other}
    >
      {svgTitle && <title>{svgTitle}</title>}
      <g className="illustration-page-404">
        <g className="illustration-page-404-2">
          <g className="ground">
            <path
              className="ellipse-20"
              opacity="0.3"
              d="M368 352C368 352 284 352 200 352C116 352 32 352 32 352C32 352 72.32 272 200 272C325.16 272 368 352 368 352Z"
              fill="url(#ground_gradient_fill_rule)"
            />
            <path
              className="grass-2"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M311.988 295.155C312.074 294.88 312.147 294.606 312.206 294.332C313.143 289.965 310.983 283.863 310.27 283.196C309.31 283.463 305.026 287.029 303.787 291.697C302.85 296.064 304.654 301.832 305.723 302.833C305.723 302.833 305.723 302.833 305.724 302.833C305.707 302.942 305.702 303.039 305.708 303.123C307.186 304.084 313.649 304.147 317.381 302.222C321.453 300.121 324.354 294.811 324.288 293.904C323.313 293.414 317.159 292.722 312.615 294.805C312.403 294.914 312.194 295.031 311.988 295.155Z"
            />
            <path
              className="tree"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M88.8749 292.925C88.7718 294.302 88.6745 295.646 88.5798 296.954C87.6037 310.441 86.9117 320 83.1932 320C80.9484 320 80.1408 313.726 79.2941 300.953C77.8338 300.902 70.5831 299.934 68.8364 293.649C67.4023 288.488 67.6496 276.338 70.8624 276.338C72.9741 276.338 73.2814 278.945 73.6429 282.012C74.0884 285.791 74.6161 290.268 78.7047 291.424C78.6955 291.27 78.6863 291.116 78.677 290.961C78.677 288.178 78.6206 285.268 78.5644 282.365L78.5644 282.365C78.3075 269.102 78.0537 256 83.1932 256C87.304 256 88.4663 265.228 88.7796 275.952C92.6072 275.19 93.1673 270.85 93.6779 266.894C94.1411 263.304 94.5636 260.03 97.3484 260.03C101.121 260.03 100.239 272.24 98.5553 277.447C96.6205 283.429 90.8684 285.481 88.8891 285.784C88.8883 286.953 88.885 288.112 88.8818 289.253V289.253V289.254C88.8783 290.505 88.8749 291.732 88.8749 292.925Z"
            />
            <path
              className="grass-1"
              d="M72.2723 307.805C74.185 311.828 74.3596 318.537 73.5248 320C72.2723 320 67.6804 316.585 65.5932 312.195C63.5059 307.317 63.9234 300.976 64.3409 300C65.1757 300 70.185 303.415 72.2723 307.805Z"
            />
          </g>
          <g className="secondary-item">
            <path
              className="rock"
              d="M248.562 300.915L270.141 302.589L284.25 312.22L281.76 321.432L272.216 328.551H248.562L239.432 321.432L238.188 309.708L248.562 300.915Z"
            />
            <path
              className="rock-2"
              d="M139.725 303.151L152.672 303.913L161.138 308.29L159.644 312.478L153.917 315.713H139.725L134.247 312.478L133.5 307.148L139.725 303.151Z"
            />
            <path
              className="bird"
              d="M316.081 127.872C319.029 124.881 323.755 124.006 330.634 125.565C332.058 125.888 332.951 127.305 332.629 128.729C332.306 130.154 330.889 131.047 329.465 130.724C323.33 129.334 320.908 130.509 319.848 131.585C318.587 132.864 318.168 134.964 317.93 137.583C317.814 138.856 316.803 139.865 315.53 139.978C314.256 140.091 313.084 139.277 312.745 138.044C312.236 136.188 310.372 135.055 307.409 135.033C305.999 135.022 304.592 135.293 303.467 135.734C302.267 136.205 301.738 136.727 301.604 136.954C300.861 138.211 299.239 138.628 297.981 137.885C296.724 137.142 296.307 135.52 297.05 134.263C298.044 132.58 299.833 131.479 301.534 130.811C303.311 130.113 305.39 129.728 307.448 129.744C309.533 129.759 311.867 130.207 313.843 131.386C314.332 130.151 315.038 128.929 316.081 127.872Z"
            />
            <path
              className="bird-2"
              d="M327.574 87.0995C330.522 84.1089 335.248 83.234 342.127 84.7929C343.552 85.1158 344.445 86.5323 344.122 87.9568C343.799 89.3813 342.383 90.2744 340.958 89.9515C334.824 88.5614 332.402 89.737 331.341 90.8128C330.08 92.0916 329.661 94.1919 329.423 96.8105C329.307 98.084 328.297 99.0923 327.023 99.2054C325.749 99.3186 324.577 98.5043 324.238 97.2712C323.729 95.4156 321.865 94.2828 318.902 94.2606C317.493 94.25 316.085 94.5204 314.96 94.962C313.761 95.4328 313.231 95.9542 313.097 96.1812C312.354 97.4387 310.732 97.8557 309.475 97.1126C308.217 96.3696 307.8 94.7478 308.543 93.4903C309.538 91.8074 311.326 90.7061 313.028 90.0383C314.804 89.3408 316.884 88.9559 318.942 88.9713C321.027 88.9869 323.361 89.4348 325.336 90.6132C325.825 89.3784 326.532 88.1568 327.574 87.0995Z"
            />
            <path
              className="cloud"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M93.2031 122.644H93.2308C96.0869 122.644 98.8261 123.769 100.846 125.772C102.865 127.774 104 130.49 104 133.322C104 136.154 102.865 138.87 100.846 140.872C98.8261 142.875 96.087 144 93.2308 144H61.2308C59.4933 144 57.7728 143.661 56.1676 143.001C54.5623 142.342 53.1038 141.376 51.8752 140.158C49.394 137.697 48 134.361 48 130.881C48 127.402 49.394 124.065 51.8752 121.605C54.2135 119.287 57.3369 117.926 60.6258 117.776C63.3421 112.011 69.1764 108 76 108C84.6231 108 91.7359 114.4 92.8265 122.666L93.2031 122.644ZM87.3846 129.356V126C87.3846 123.006 86.1852 120.135 84.0501 118.018C81.9151 115.901 79.0194 114.712 76 114.712C70.3082 114.712 65.6391 118.819 64.7508 124.172L64.5702 125.26L63.5245 124.883C62.8053 124.624 62.0257 124.475 61.2308 124.475C59.5171 124.475 57.8735 125.15 56.6618 126.351C55.45 127.553 54.7692 129.182 54.7692 130.881C54.7692 132.581 55.45 134.21 56.6618 135.412C57.8735 136.613 59.5171 137.288 61.2308 137.288H93.2308C94.2916 137.288 95.3091 136.87 96.0592 136.126C96.8093 135.383 97.2308 134.374 97.2308 133.322C97.2308 132.27 96.8093 131.261 96.0592 130.518C95.3091 129.774 94.2916 129.356 93.2308 129.356H87.3846Z"
            />
            <g className="sun">
              <path
                className="sunset"
                fillRule="evenodd"
                clipRule="evenodd"
                d="M281.281 132.99L222.209 125H177.85L116.593 132.365C124.207 170.923 158.208 200 199 200C239.571 200 273.425 171.237 281.281 132.99ZM282.251 104.732L222.209 111.5H177.85L115.719 104.964C121.122 63.7874 156.349 32 199 32C241.571 32 276.746 63.6684 282.251 104.732Z"
              />
            </g>
          </g>
          <g className="light-house-primary-item">
            <g className="body">
              <mask
                id="mask0-4418-68295"
                className="light-house-primary-mask"
                style={{ maskType: 'alpha' }}
                maskUnits="userSpaceOnUse"
                x="151"
                y="135"
                width="98"
                height="170"
              >
                <path
                  className="rectangle-58"
                  d="M178.034 135.579H221.966L249 304C249 304 223.655 300.72 199.155 300.72C177.612 300.72 151 304 151 304L178.034 135.579Z"
                  fill="orange"
                />
              </mask>
              <g mask="url(#mask0-4418-68295)">
                <path
                  className="rectangle-49"
                  d="M177.834 135.579H221.765L248.799 304C248.799 304 223.454 300.72 198.954 300.72C177.411 300.72 150.799 304 150.799 304L177.834 135.579Z"
                />
                <path
                  className="rectangle-53"
                  d="M153.534 264.078L240.974 234.105L243.508 250.079L150.155 282.079L153.534 264.078Z"
                />
                <path
                  className="rectangle-56"
                  d="M162.828 213.895L235.06 188.632L236.75 205.053L159.871 232L162.828 213.895Z"
                />
                <path
                  className="rectangle-57"
                  d="M168.319 169.027L228.302 148.048L230.836 164.004L166.629 186.46L168.319 169.027Z"
                />
                <rect
                  className="rectangle-52"
                  x="192.933"
                  y="280.421"
                  width="13.5172"
                  height="21.8947"
                  fill="var(--color-gray-400)"
                />
              </g>
            </g>
            <path className="rectangle-33" d="M186.718 105H212.91H230V131H211.053H186.718H170V105H186.718Z" />
            <path
              className="windows"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M178.034 112H188.172V125.474H178.034V112ZM194.931 112H205.069V125.474H194.931V112ZM221.965 112H211.827V125.474H221.965V112Z"
            />
            <path
              className="top"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M199.956 64C196.632 64 193.937 66.6949 193.937 70.0191V78.7561C183.552 81.4203 175.879 90.8169 175.879 102H199.956H224.032C224.032 90.817 216.359 81.4204 205.975 78.7561V70.0191C205.975 66.6949 203.28 64 199.956 64Z"
            />
          </g>
        </g>
      </g>
      <defs>
        <linearGradient
          className="ground-gradient"
          id="ground_gradient_fill_rule"
          x1="200"
          y1="272"
          x2="200"
          y2="352"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--color-gray-300)" />
          <stop offset="0.390625" stopColor="var(--color-gray-200)" />
          <stop offset="1" stopColor="var(--color-gray-200)" stopOpacity="0" />
        </linearGradient>
      </defs>
    </StyledPageNotFoundIllustration>
  );
};

PageNotFoundIllustration.propTypes = {
  /** Adds additional class to the component */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Adjusts the size of the svg based upon the width. The aspect ratio is locked. */
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Adds SVG accessibility title element */
  svgTitle: PropTypes.string,
};

export { PageNotFoundIllustration };
